import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../Components/Navbar/navbar";
import LinkIcon from "@mui/icons-material/Link";
import { temPermissaoRota } from "../../Hooks/temPermissaoRota";


import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import HistoryIcon from "@mui/icons-material/History";

import { db, storage } from "../Config/firebase";
import {
  collection,
  getDocs,
} from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { consultaTenantAtual } from "../Utils/tenant";
import { getHistoricoByDocumento } from "./sgqService";

/* =========================
   UTIL
========================= */
function extrairSequenciaCodigoISO(codigoISO = "") {
  // Ex: "RQ 1.2" -> [1, 2]
  const parteNumerica = codigoISO.split(" ")[1];
  if (!parteNumerica) return [];

  return parteNumerica
    .split(".")
    .map(n => parseInt(n, 10));
}

function formatDate(timestamp) {
  if (!timestamp?.toDate) return "";
  return timestamp.toDate().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function SGQ() {
  /* =========================
     STATES
  ========================= */
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const tipoUsuario = localStorage.getItem("tipoUsuario");


  const [filter, setFilter] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const [openHistory, setOpenHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  /* =========================
     LOAD FIRESTORE
  ========================= */
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        const categoriasSnap = await getDocs(
          consultaTenantAtual(collection(db, "sgq_categorias"))
        );

        const documentosSnap = await getDocs(
          consultaTenantAtual(collection(db, "sgq_documentos"))
        );

        const cats = categoriasSnap.docs
          .map(d => ({ id: d.id, ...d.data(), itens: [] }))
          .filter(categoria => categoria.ativa !== false)
          .sort((a, b) => Number(a.ordem || 0) - Number(b.ordem || 0));

        documentosSnap.docs.forEach(d => {
          const doc = { id: d.id, ...d.data() };
          if (doc.ativo === false) return;
          const cat = cats.find(c => c.codigo === doc.categoriaCodigo);
          if (cat) cat.itens.push(doc);
        });

        setCategorias(cats);
      } catch (e) {
        console.error(e);
        setSnackbar({
          open: true,
          message: "Erro ao carregar SGQ",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  /* =========================
     FILTER
  ========================= */
  const filtered = useMemo(() => {
    if (!filter) return categorias;

    const q = filter.toLowerCase();

    return categorias
      .map(cat => ({
        ...cat,
        itens: cat.itens.filter(doc =>
          contemTextoPesquisa(`${doc.codigoISO} ${doc.titulo}`.toLowerCase(), q)
        ),
      }))
      .filter(cat => cat.itens.length > 0);
  }, [filter, categorias]);

  /* =========================
     LOAD PDF ATUAL
  ========================= */
  useEffect(() => {
    async function loadPdf() {
      if (!selectedDoc?.storagePath) {
        setPdfUrl(null);
        return;
      }

      try {
        const url = await getDownloadURL(
          ref(storage, selectedDoc.storagePath)
        );
        setPdfUrl(url);
      } catch {
        setPdfUrl(null);
      }
    }

    loadPdf();
  }, [selectedDoc]);

  /* =========================
     LOAD HISTÓRICO
  ========================= */
  async function loadHistory() {
    if (!selectedDoc?.id) return;

    try {
      const itens = await getHistoricoByDocumento(selectedDoc.id);
      setHistory(itens);
      setOpenHistory(true);
    } catch (e) {
      console.warn(e);
      setHistory([]);
      setOpenHistory(true);
    }
  }

  /* =========================
     OPEN PDF HISTÓRICO
  ========================= */
  async function abrirPdfHistorico(path) {
    try {
      const url = await getDownloadURL(ref(storage, path));
      window.open(url, "_blank");
    } catch (e) {
      setSnackbar({
        open: true,
        message: "Erro ao abrir PDF da revisão",
        severity: "error",
      });
    }
  }

  /* =========================
     RENDER
  ========================= */
  return (
    <>
      <Navbar />

      <Box sx={{ mt: "10px", px: 2 }}>
        <Box sx={{ display: "flex", gap: 3 }}>
          {/* MENU */}
          <Box sx={{ width: 340 }}>
            <Card>
              <CardContent>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar documento..."
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                  }}
                />

                <Divider sx={{ my: 2 }} />

                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  filtered.map(cat => (
                    <Accordion key={cat.id} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>
                          {cat.codigo}. {cat.titulo}
                        </Typography>
                      </AccordionSummary>

                     <AccordionDetails sx={{ p: 0 }}>
                            <List dense>
                              {[...cat.itens]
                                .sort((a, b) => {
                                  // Extrai números de forma segura (ignora letras e espaços invisíveis)
                                  const aMatch = a.codigoISO.match(/(\d+(?:\.\d+)*)/);
                                  const bMatch = b.codigoISO.match(/(\d+(?:\.\d+)*)/);

                                  const aSeq = aMatch ? aMatch[1].split(".").map(Number) : [];
                                  const bSeq = bMatch ? bMatch[1].split(".").map(Number) : [];

                                  const maxLen = Math.max(aSeq.length, bSeq.length);

                                  for (let i = 0; i < maxLen; i++) {
                                    const aVal = aSeq[i] ?? 0;
                                    const bVal = bSeq[i] ?? 0;

                                    if (aVal !== bVal) return aVal - bVal;
                                  }

                                  return 0;
                                })
                                .map(doc => (
                                  <ListItemButton
                                    key={doc.id}
                                    onClick={() => {
                                      if (doc.origemDocumento === "rota" && doc.rotaInterna) {
                                        const permitido = temPermissaoRota(
                                          doc.rotaInterna,
                                          tipoUsuario
                                        );

                                        if (!permitido) {
                                          setSnackbar({
                                            open: true,
                                            message:
                                              "Acesso negado. Você não possui permissão para acessar este conteúdo.",
                                            severity: "error",
                                          });
                                          return;
                                        }

                                        window.location.href = doc.rotaInterna;
                                        return;
                                      }

                                      setSelectedDoc(doc);
                                    }}
                                  >
                                    <ListItemIcon>
                                      {doc.origemDocumento === "rota" ? (
                                        <LinkIcon color="primary" />
                                      ) : (
                                        <PictureAsPdfIcon color="error" />
                                      )}
                                    </ListItemIcon>

                                    <ListItemText
                                      primary={
                                        <Typography
                                          sx={{
                                            color: "primary.main",
                                            cursor: "pointer",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {`${doc.codigoISO} — ${doc.titulo}`}
                                        </Typography>
                                      }
                                    />
                                  </ListItemButton>
                                ))}
                            </List>
                          </AccordionDetails>


                                                  </Accordion>
                                                ))
                                              )}
                                            </CardContent>
                                          </Card>
                                        </Box>

                                        {/* CONTEÚDO */}
                                        <Box sx={{ flex: 1 }}>
                                          {selectedDoc && (
                          <Card>
                            <CardContent>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                              >
                                <Typography variant="h5" fontWeight={700}>
                                {selectedDoc.titulo}
                              </Typography>

                              <Button
                                variant="outlined"
                                startIcon={<HistoryIcon />}
                                onClick={loadHistory}
                              >
                                Histórico
                              </Button>
                                    </Stack>

                                    {pdfUrl && (
                                      <iframe
                                        src={pdfUrl}
                                        title="preview"
                                        style={{
                                          width: "100%",
                                          height: "75vh",
                                          border: "none",
                                        }}
                                      />
                                    )}
                                  </CardContent>
                                </Card>
                  )}
          </Box>
        </Box>
      </Box>

      {/* HISTÓRICO */}
      <Dialog
        open={openHistory}
        onClose={() => setOpenHistory(false)}
        fullWidth
      >
        <DialogTitle>Histórico de Versões</DialogTitle>
        <DialogContent>
          {history.length === 0 ? (
            <Typography color="text.secondary">
              Nenhuma versão registrada
            </Typography>
          ) : (
            <List>
              {history.map(h => (
                <ListItemButton
                  key={h.id}
                  onClick={() => h.storagePath && abrirPdfHistorico(h.storagePath)}
                >
                  <ListItemIcon>
                    <PictureAsPdfIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={h.acao === "edicaoDados" ? `Edição de dados — Revisão ${h.revisao}` : `Revisão ${h.revisao}`}
                    secondary={
                      <>
                        <Typography component="span" variant="body2">
                          Responsável: {h.userName || h.userEmail || "Usuário não identificado"}
                          {h.userEmail && h.userEmail !== h.userName ? ` (${h.userEmail})` : ""}
                        </Typography>
                        <br />
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {formatDate(h.createdAt)}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar(prev => ({ ...prev, open: false }))
        }
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
